/**
 * Виджеты категории "Продуктивность": список задач, заметки, трекер привычек,
 * калькулятор, помодоро, карточки, расписание, Google Calendar.
 *
 * Факты — по версии расширения 4.0.0:
 * data/screenshots/report-4-time-productivity.md и report-4-new-widgets.md.
 *
 * @see docs/seo/SEO-STRATEGY.md
 * @see docs/SEO_WRITER.md
 */

export default [
	{
		slug: 'todo-list',
		name: 'To-Do List',
		category: 'Productivity',
		plan: 'free',
		model: 'TodoModel',
		title: 'To Do List Widget for Chrome New Tab',
		metaDescription:
			'A to do list Chrome extension widget for the new tab page: a Today and Later planner, priorities, due dates, auto-archive and a 30-day chart. No account.',
		h1: 'To do list widget for your Chrome new tab',
		lead:
			'A to do list widget that lives on your Chrome new tab, so the list is in front of you every time you open a tab — usually the exact moment you were about to forget something. It plans now as well, with Today and Later, priorities and due dates.',
		screenshot: {
			file: 'todo-list-hero.webp',
			alt:
				'Todo list in the Planner design with Today, Later and Done sections, an overdue task in red, priority dots and due dates',
			caption: 'Today, Later and Done, with the overdue one in red',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A planner with Today, Later and Done',
				p: [
					'Pick the Planner design and the list splits in three. Today is what you’re on now, Later is everything else, and Done folds itself shut so finished work doesn’t crowd the page.',
					'Give a task a due date and it waits in Later until that day comes, then moves up to Today by itself. The check runs at midnight, no reload needed. Drag a task by hand and your call beats the date. Due dates are calendar days rather than hours, so there is no "by 3 pm".',
				],
			},
			{
				h2: 'Priorities, due dates and sorting',
				p: [
					'Switch on Priority and due date in the settings and each task can carry a coloured dot and a date, both set from its ⋯ menu. Priority goes No priority, Low, Medium, High. Due date offers Today, Tomorrow, In a week or any day you pick.',
					'Order does the rest. My order is yours to arrange by dragging. Priority and Due date sort the list for you, push finished tasks to the bottom and switch dragging off, since the sort owns the order now. Planner lets you drag either way.',
					'A new list arrives with three sample tasks, one of them High and due today, so you can see how it all fits before typing anything.',
				],
				image: {
					file: 'todo-list-task-menu.webp',
					alt:
						'Task menu open in a todo list: priority set to High, due date chips, Move to Later and Delete',
					caption:
						'Priority, due date and a move to Later, all in one menu',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Where finished tasks go',
				p: [
					'In My order, with Show completed tasks on, a ticked task stays where it was, struck through. That’s a nice sight at four in the afternoon. Turn the switch off and ticked items leave the list instead; in Planner they drop into Done.',
					'Move completed tasks to the archive tidies up for you, Next day or After a week (or Never). Archived tasks leave the list but stay in the statistics. You can also delete a finished task outright, and Delete means gone, chart included.',
				],
			},
			{
				h2: 'The 30-day chart and the archive',
				p: [
					'The chart icon in the header opens a bar chart of the last 30 days with the finished tasks listed underneath. Each has Return to the list, Uncheck and Delete. Clear archive empties the lot after asking, and those tasks leave the chart too.',
					'The archive is not bottomless. It keeps the last 500 tasks for up to a year, and anything still on the 30-day chart is never dropped early. Plenty for a personal list. If you need a paper trail going back years, you need a different tool.',
				],
			},
			{
				h2: 'Several lists, seven designs',
				p: [
					'Tasks drag between widgets, not only inside one, so three lists called To do, Doing and Done make a kanban board in about a minute. The newer designs count progress in the header, 3/7 style, which is oddly motivating on a long day.',
					'The designs are Classic, Glass, Light, Dark, Paper, Planner and Minimal, switched from the Design button or the top of the settings window. A new list starts in Glass (Light or Dark on a plain colour background). Lists you already had stay in Classic until you pick something else.',
				],
				image: {
					file: 'todo-kanban.webp',
					alt:
						'Three todo lists as a kanban board, with a task being dragged from To do into Doing',
					caption:
						'Three lists make a board. Tasks drag between them',
					width: 1440,
					height: 900,
					small: true,
				},
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Seven looks. Glass for a new list; lists from before the redesign keep Classic.',
			},
			{
				name: 'Title',
				detail:
					'Up to 70 characters. On a board it doubles as the column name.',
			},
			{
				name: 'Priority and due date',
				detail:
					'A coloured dot and a date on each task, set from its ⋯ menu. Every design has it except Classic.',
			},
			{
				name: 'Order',
				detail:
					'My order, Priority or Due date. Dragging works in My order and in Planner.',
			},
			{
				name: 'Show completed tasks',
				detail:
					'Off hides ticked tasks instead of striking them through.',
			},
			{
				name: 'Move completed tasks to the archive',
				detail:
					'Never, Next day or After a week. Runs when a tab opens and again at midnight.',
			},
			{
				name: 'Accent color',
				detail:
					'Eight options, the first one matching the design. Not in Classic.',
			},
			{
				name: 'Open statistics',
				detail:
					'The 30-day chart and the archive. The chart icon in the header opens the same window.',
			},
		],
		useCases: [
			'A Planner list in the middle of a minimal page.',
			'A three-column kanban board, To do / Doing / Done.',
			'One list per client or subject, sorted by due date.',
			'A daily routine that archives itself the next morning.',
			'A packing list on a workspace you only open at home.',
		],
		faq: [
			{
				q: 'Is there a free digital to-do list?',
				a:
					'Yes, Google Tasks, Microsoft To Do and Apple Reminders are all free digital to-do lists, and so is the to-do widget in New Tab Studio, which lives on your Chrome new tab with no account to create. Its Planner design splits the day into Today, Later and Done. The free plan lets you place three widgets by hand, so three lists if lists are all you need.',
			},
			{
				q: 'How do I put a to-do list on my Chrome new tab?',
				a:
					'Chrome’s standard new tab page has no to-do list, so you need an extension that replaces it. With New Tab Studio installed, press the button in the bottom-left corner of any new tab and pick Todo list under Productivity. It arrives with three sample tasks, one marked High and due today, so you see how priorities look before typing your own.',
			},
			{
				q: 'What should a daily to-do list include?',
				a:
					'A good daily to-do list holds only what you can realistically finish today, each task starting with a verb and the one that matters most at the top. Everything else belongs on a separate “later” list. The Planner design works that way, and a task with a due date moves from Later to Today by itself when its day comes.',
			},
			{
				q:
					'Does the to-do list sync with Todoist, Google Tasks or my other computer?',
				a:
					'No, the to-do widget has no account and no live sync with Todoist, Google Tasks or another computer. Tasks are stored in this browser with the rest of your page, so adding and ticking work offline. Exporting the page or publishing it as a theme carries the tasks along, handy for moving machines and a good reason to tidy up before you share.',
			},
			{
				q: 'Do finished tasks disappear?',
				a:
					'By default, finished tasks in the to-do widget stay on the list, struck through, until you decide otherwise. Switch off Show completed tasks to hide them, or let Move completed tasks to the archive clear them the next day or after a week. Archived tasks still show in the 30-day chart, while Delete takes a task off the chart as well.',
			},
		],
		related: ['notes', 'pomodoro', 'calendar', 'habit-tracker'],
		keywords: [
			'to do list chrome extension',
			'to do list widget',
			'new tab to do list',
			'chrome new tab todo list',
			'kanban new tab',
		],
	},

	{
		slug: 'notes',
		name: 'Notes',
		category: 'Productivity',
		plan: 'free',
		model: 'NoteModel',
		title: 'Sticky Notes Chrome Extension for New Tab',
		metaDescription:
			'A sticky notes Chrome extension for the new tab: checklists you tick in place, lists, links, five designs and a handwriting font. Stored locally as plain text.',
		h1: 'Chrome sticky notes on every new tab',
		lead:
			'Sticky notes for Chrome that sit on the new tab page, right where you’ll look next. A meeting ID, a phone number, the first line of an email you’re dreading. A note can hold a checklist now too, and you tick it without opening the editor.',
		screenshot: {
			file: 'notes-hero.webp',
			alt:
				'Three sticky notes on a table: a checklist titled Today, a reading list with a link, and a reminder',
			caption: 'Checklists, links and plain text, each on its own sticky',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'A note that looks like a note',
				p: [
					'Add a note and it turns up as a Sticky note, a pastel square tilted a degree or two. The tilt is worked out from the moment the note was created, so each one keeps its own lean for good and a row of them looks stuck on by hand.',
					'The Design button has four more looks, Classic, Glass, Paper and Minimal. Colour picks apply to Sticky note and Classic; the other three bring their own background. Notes you had before the update stay Classic, untouched.',
				],
				image: {
					file: 'notes-skins.webp',
					alt:
						'The same grocery checklist in five note designs: Classic, Sticky note, Glass, Paper and Minimal',
					caption: 'One list, five kinds of paper',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Checklists, lists and links',
				p: [
					'Start a line with - [ ] and it becomes a checkbox you can tick right on the note. A dash or an asterisk makes a bullet, # makes a heading, double asterisks around a word make it bold, and anything starting with https:// or www. turns into a link that opens in a new tab.',
					'Ctrl+B (Cmd+B on a Mac) wraps the selection in them for you. Enter carries a list on to the next line, and Enter on an empty item ends it. The Add a checklist item button saves you typing the brackets.',
					'Don’t want any of it? Switch off Checklists and links and the note is plain text again. Underneath it always was. The formatting is just characters on a line, which is also why a note travels cleanly inside an exported or published theme.',
				],
			},
			{
				h2: 'Note or to-do list?',
				p: [
					'A checklist in a note is a scratch list. Five lines, ticked, forgotten. The To-Do List widget is for tasks that need a due date, a priority, an archive or a chart, and if your note is heading for forty checkboxes, you wanted the other widget.',
					'What notes won’t do is nested pages, encryption or live sync between computers. It’s a sticky note, not a notebook, and it is the wrong place for a password.',
				],
			},
			{
				h2: 'Size, font and saving',
				p: [
					'Note settings keeps it short. Font size runs Small, Medium, Large, Extra large, and the font is Default, Handwriting (Caveat) or Monospace (JetBrains Mono). Handwriting on a Sticky note is the obvious combo; Monospace is for commands and codes you copy out.',
					'Text saves a moment after you stop typing and again when you click away. A long note scrolls inside its frame, and there is no length cap on a single note. The ceiling is the browser storage your whole page shares, and the extension will tell you if you ever hit it.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Sticky note, Glass, Paper or Minimal. A new note starts as Sticky note.',
			},
			{
				name: 'Color',
				detail:
					'Seven pastels, for Sticky note and Classic. New notes get one at random, so the same colour twice in a row can happen.',
			},
			{
				name: 'Font size',
				detail:
					'Small, Medium, Large or Extra large. Choose for the distance you read from, not the amount of text.',
			},
			{
				name: 'Font',
				detail:
					'Default, Handwriting (Caveat) or Monospace (JetBrains Mono).',
			},
			{
				name: 'Checklists and links',
				detail:
					'On by default. Turns - [ ] into checkboxes, dashes into bullets and web addresses into links.',
			},
			{
				name: 'Character count',
				detail:
					'Off by default. A running count in the corner, handy when you draft a bio or a post with a limit.',
			},
			{
				name: 'Toolbox',
				detail:
					'Fill colour on Sticky note only, text colour on Sticky note and Minimal. Classic keeps its full old set.',
			},
		],
		useCases: [
			'Meeting IDs and passcodes you use every week.',
			'Three checkboxes for today, done by lunch.',
			'A handful of links you open every morning.',
			'A command cheat sheet in Monospace.',
			'The draft of a message you’re still thinking about.',
		],
		faq: [
			{
				q: 'Are sticky notes good for ADHD?',
				a:
					'Many people with ADHD use sticky notes, and some find them one of the few reminders that stick, because a note in plain sight doesn’t depend on remembering to open an app. The catch is that paper notes pile up until you stop seeing them. A note on the new tab turns up with every tab you open, and it can hold a checklist you tick in place.',
			},
			{
				q: 'How do I add sticky notes to my Chrome new tab page?',
				a:
					'Chrome has no sticky notes of its own, so adding them to the new tab takes an extension such as New Tab Studio. On a new tab, use the button in the bottom-left corner to open the widget panel and choose Notes under Productivity. The page is then in edit mode, so double-click the note to type. For the desktop itself, Windows has Sticky Notes and macOS has Stickies.',
			},
			{
				q: 'Is there a free sticky notes extension for Chrome?',
				a:
					'Yes, the Notes widget in New Tab Studio is free, with all five designs, seven pastel colours and the handwriting font. Each note is a widget of its own, and the free plan lets you place three by hand, so three notes or two notes and something else. Notes that came with an installed theme don’t use up that allowance.',
			},
			{
				q: 'Where are my notes saved?',
				a:
					'The Notes widget saves your notes as plain text in this browser’s local storage, a moment after you stop typing and again when you click away. They are not encrypted and don’t sync to other devices, so keep passwords somewhere else. Exporting your page or publishing it as a theme takes the notes along, and that’s how you move them to another machine.',
			},
			{
				q: 'Can I use markdown or checklists in a note?',
				a:
					'Yes, the Notes widget understands a small slice of markdown, from - [ ] checkboxes and bullets to # headings, bold text and clickable links. It’s all on by default, and the Checklists and links switch turns it off. Underneath, the note is still plain text, so nothing breaks when it travels inside a theme.',
			},
		],
		related: ['todo-list', 'text', 'link', 'calculator'],
		keywords: [
			'sticky notes chrome extension',
			'chrome sticky notes',
			'notes chrome extension',
			'new tab notes',
			'sticky notes new tab',
		],
	},

	{
		slug: 'habit-tracker',
		name: 'Habit Tracker',
		category: 'Productivity',
		plan: 'mixed',
		model: 'HabitModel',
		title: 'Habit Tracker Widget for Chrome New Tab',
		metaDescription:
			'A habit tracker widget for the Chrome new tab: set a goal in days, allow a few skips, check in with one click. Six designs, from a streak grid to a plant.',
		h1: 'Habit tracker widget for your Chrome new tab',
		lead:
			'A habit tracker widget on the page Chrome shows you all day, so checking in costs one click and zero willpower. Set a goal, allow yourself a few skips, then watch a ring fill, a streak grid fill in or a small plant grow.',
		screenshot: {
			file: 'habit-tracker-hero.webp',
			alt:
				'Habit tracker card for Read showing 12 of 30 days and a 5-day streak next to a todo list',
			caption: 'Twelve days down, eighteen to go, five in a row',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Goal days and skip days',
				p: [
					'Two numbers define a tracker: how many days you’re aiming for, and how many you’re allowed to miss. Thirty and ten out of the box, anything up to 365 either way. The skip allowance is what stops one bad week from killing the habit.',
					'Skips are not something you press. The tracker works them out from the calendar, and each time one gets used up it pops up "You skipped the day" with the number left. Disable reminder turns that off.',
					'Press Start in the settings and day one begins. From then on the goal and the skip count are locked, because moving the goalposts mid-run is the exact thing a tracker is there to stop.',
				],
			},
			{
				h2: 'Six designs, one habit',
				p: [
					'Ring is the original circle, and trackers you already had stay in it. A new one arrives as Card, a glass card with the habit name, a streak chip with a flame, a ring reading 12 of 30 and a big Check in button.',
					'Streak draws the last 18 weeks as a grid of squares, GitHub-style, with your current streak, your best and the days still to go. Minimal is a big "Day 12 / 30" and a thin bar. Chain puts a dot on every goal day and links up the ones you’ve done.',
					'Plant is the soft option — leaves sprout as you go, a bud shows at 85 % and it flowers on the day you hit the goal. Its button says Water instead of Check in.',
				],
				image: {
					file: 'habit-tracker-designs.webp',
					alt:
						'Habit tracker settings with live previews of six designs: Ring, Card, Streak, Minimal, Chain and Plant',
					caption: 'Six ways to draw the same habit',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'History and the check-in',
				p: [
					'The tracker keeps the actual days you checked in, not just a running total, and the streak and best streak are built from that. Older trackers get their history rebuilt from the old counter, so the grid isn’t empty after the update.',
					'You can mark today, once, with a little burst of confetti. Hit it by mistake? Today’s check-in can be undone. Yesterday cannot be filled in afterwards, which is harsh on forgetful days and the reason the number means something.',
				],
				image: {
					file: 'habit-tracker-streak.webp',
					alt:
						'Habit streak grid with seven days in a row, one missed day and five more, showing streak 5 and best 7',
					caption: 'One missed day, and the streak starts over',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Finishing, failing, starting over',
				p: [
					'Reach the goal and you get one of ten animated GIFs, never the same one twice running. Run out of skips and the only way on is Try again. Progress goes back to zero and the settings open for a fresh start.',
					'The settings window keeps score, "Completed N days, skipped M days", with start and end dates and a percentage. Restart begins again from today and Stop habit ends the run; both ask first if there’s progress to lose.',
				],
			},
			{
				h2: 'One tracker free, more on PRO',
				p: [
					'The free plan runs one habit tracker, and this rule is stricter than the usual limit of three hand-added widgets. Trackers that came with an installed theme count as well. Install a theme with three of them and the second and third show an "enable PRO version" plaque until you upgrade.',
					'Everything else is free. All six designs, the history, the streaks, the plant. One habit is a fair test of whether the widget works for you.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Ring, Card, Streak, Minimal, Chain or Plant. New trackers start as Card.',
			},
			{
				name: 'Title',
				detail:
					'Up to 12 characters, so "Read" fits and "Read 20 pages" doesn’t.',
			},
			{
				name: 'Number of days to repeat the habit',
				detail:
					'The goal, from 1 to 365; 30 by default. Locked while the habit runs.',
			},
			{
				name: 'How many skip days you need?',
				detail:
					'From 0 to 365, ten by default. Also locked once you press Start.',
			},
			{
				name: 'Restart and Stop habit',
				detail:
					'Restart moves day one to today, Stop habit ends the run. Both confirm before wiping progress.',
			},
			{
				name: 'Progress',
				detail:
					'Completed and skipped days, start and end dates, percentage done.',
			},
			{
				name: 'Border',
				detail:
					'On Ring and Card it draws the circle. Streak, Chain and Plant use only its colour, as the accent (mint by default), and Minimal borrows its thickness for the bar.',
			},
		],
		useCases: [
			'Thirty days of workouts with ten rest days to spend.',
			'Language or instrument practice on a Streak grid.',
			'Reading every evening, one watering at a time.',
			'Quitting sugar, smoking or late-night scrolling.',
		],
		faq: [
			{
				q: 'Is there a free habit tracker for Chrome?',
				a:
					'Yes, New Tab Studio’s habit tracker widget is free for one habit, with all six designs, the streak history and a one-click check-in on every new tab. A second tracker needs PRO, and that includes trackers bundled with a theme you installed. One habit is a fair test of whether seeing it on every tab keeps you going.',
			},
			{
				q: 'Do habit trackers actually work?',
				a:
					'Habit trackers tend to work when logging takes no effort and the tracker stays in view; one you have to remember to open usually gets forgotten along with the habit. The tracker doesn’t build the habit — the repetition does — but a visible streak gives you a reason not to break it. On the new tab, checking in is one click on a page you open all day anyway.',
			},
			{
				q: 'Are habit trackers good for ADHD?',
				a:
					'Habit trackers can suit people with ADHD when the tracker sits in plain sight and one missed day doesn’t wipe out the run. Some people with ADHD find all-or-nothing streaks discouraging, and that’s where skip days come in. This tracker allows ten skips on a 30-day goal by default, so a bad day costs one skip rather than the whole run.',
			},
			{
				q: 'Can I mark yesterday as done?',
				a:
					'No, the habit tracker only lets you check in for today, once. If you click by mistake, today’s check-in can be undone, but a day that has passed can’t be filled in afterwards. It’s harsh on forgetful evenings, and it’s also why the count on the card means something.',
			},
			{
				q: 'Does missing a day reset everything?',
				a:
					'Missing a day doesn’t reset the habit tracker while you have skip days left. Skips are worked out from the calendar, and each time one gets used up the tracker tells you how many remain. Your in-a-row streak does start over, though, and once the skips run out, Try again sends progress back to zero and opens the settings for a fresh start.',
			},
		],
		related: ['count-up', 'todo-list', 'pomodoro', 'flash-cards'],
		keywords: [
			'habit tracker widget',
			'habit tracker chrome extension',
			'new tab habit tracker',
			'streak tracker new tab',
		],
	},

	{
		slug: 'calculator',
		name: 'Calculator',
		category: 'Productivity',
		plan: 'free',
		model: 'CalculatorModel',
		title: 'Calculator Chrome Extension for New Tab',
		metaDescription:
			'A calculator extension for Chrome that lives on the new tab: desk-calculator maths, keyboard input on hover and six designs, Retro LCD included. Works offline.',
		h1: 'A calculator extension for Chrome, on your new tab',
		lead:
			'A calculator Chrome extension without a popup to hunt for. It sits on the new tab page, so the sum you need is one tab away rather than one search away. Six designs, keyboard input while you hover, and maths that behaves like the calculator on your desk.',
		screenshot: {
			file: 'calculator-hero.webp',
			alt:
				'Glass calculator showing 1,250 × 0.15 = 187.5 next to a sticky note with an invoice total',
			caption: 'The VAT, worked out on the tab, written down on a sticky',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Maths like a desk calculator',
				p: [
					'It works left to right, the way a pocket calculator does, so 2 + 3 × 4 gives 20, not 14. Want the school-maths answer? Do the multiplication first.',
					'Press = again and it repeats the last step. Handy when you’re adding the same amount a few times over. It keeps 15 digits of precision, so 0.1 + 0.2 comes out as 0.3 instead of the long tail of decimals a browser console gives you.',
					'Percent works the way shop assistants expect: 50 + 10 % is 55, 50 × 10 % is 5. C clears the number and a second press clears everything, ⌫ removes one digit, ± flips the sign.',
				],
			},
			{
				h2: 'Type while you hover',
				p: [
					'Put the pointer over the calculator and type. No clicking required. Digits, + - * / (x works for times too), Enter or = for the result, a comma or a dot for decimals, % and Backspace. Esc clears, F9 flips the sign.',
					'Move the pointer away and the keyboard goes back to the rest of the page, so the calculator never eats what you’re typing in a note. With two calculators on screen, only the one under the pointer listens, and anything typed with Ctrl, Cmd or Alt held is left alone.',
				],
			},
			{
				h2: 'Six designs and an accent',
				p: [
					'Classic, Midnight, Glass, Soft, Retro LCD and Pastel, each with its own typeface. Retro LCD draws seven-segment digits like a 1980s desk model. A new calculator arrives in Glass at a slightly reduced size; older ones keep Classic.',
					'In the newer designs the fill colour from the toolbox tints the operator keys, the quickest way to match a wallpaper. There’s no text colour option, and bold or italic only exist in Classic.',
					'Above the result sits the last expression, 12 + 3 = and so on (Retro LCD leaves it out). That’s the whole memory. Reload the tab and the display is back at 0.',
				],
				image: {
					file: 'calculator-skins.webp',
					alt:
						'Six calculator designs side by side: Classic, Midnight, Glass, Soft, Retro LCD and Pastel',
					caption:
						'Six calculators. The math is the same in all of them',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Your numbers stay put',
				p: [
					'The figures never leave the page. The extension’s analytics does note that a calculation happened, at most once every ten seconds, but not what you calculated. Typing the same sum into the address bar hands it to your search engine instead.',
					'It works offline, since every sum is done locally. It is not scientific either — no brackets, no powers, no memory keys. When you need those, an iframe widget can hold a full calculator site on the same tab.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Midnight, Glass, Soft, Retro LCD or Pastel. New calculators start in Glass.',
			},
			{
				name: 'Keyboard',
				detail:
					'Active while the pointer is over the calculator. Esc clears, F9 flips the sign, comma or dot for decimals.',
			},
			{
				name: 'Keys',
				detail:
					'C/AC, ⌫, %, ÷, ×, −, +, ±, the digits, a decimal point and =. The newer designs lay them out as a 4 × 5 grid.',
			},
			{
				name: 'Fill colour',
				detail:
					'Tints the operator keys in the newer designs. There’s no separate text colour.',
			},
			{
				name: 'Size and position',
				detail:
					'Drag and resize like any widget. It looks right small, in a corner.',
			},
		],
		useCases: [
			'Splitting a bill or working out a tip.',
			'Adding tax or a discount with the percent key.',
			'Invoice totals, with a note beside it to keep the result.',
			'Repeating the same step with a second press of =.',
		],
		faq: [
			{
				q: 'Does Chrome have a built-in calculator?',
				a:
					'Chrome has no calculator app of its own, but with Google as your search engine you can type a sum like 1250*0.15 into the address bar and the answer shows up in the suggestions. Windows and macOS each come with a Calculator app too. The calculator widget in New Tab Studio is for when you want one sitting on the new tab, ready for the keyboard.',
			},
			{
				q: 'Is the calculator widget free, and where do I find it?',
				a:
					'The calculator widget is free with all six designs, and it hides under Common rather than Productivity. Hit the button in the bottom-left corner, then Common, then Calculator. The free plan caps you at three self-added widgets of any kind, and a calculator is one of them.',
			},
			{
				q: 'Is the calculator scientific?',
				a:
					'No, the calculator widget is a desk calculator with the four operations, percent and a sign key, plus 15 digits of precision so 0.1 + 0.2 comes out as 0.3. There are no brackets, powers or memory keys. For trigonometry and friends, an iframe widget can hold a full calculator site on the same tab.',
			},
			{
				q: 'Can I type instead of clicking?',
				a:
					'Yes, the calculator takes keyboard input while the pointer is over it. Digits, + - * /, Enter or = for the result, % and Backspace all work, Esc clears and F9 flips the sign. Move the pointer away and your keystrokes go back to the rest of the page, so typing in a note never lands in a sum.',
			},
			{
				q: 'Does the calculator keep a history?',
				a:
					'No, the calculator only shows the last expression above the result, 12 + 3 = and so on. Nothing else is saved, so reloading the tab puts the display back at 0. For a number worth keeping, put a note beside the calculator and copy it there before you close anything.',
			},
		],
		related: ['notes', 'currency', 'iframe', 'workspaces'],
		keywords: [
			'calculator chrome extension',
			'calculator extension for chrome',
			'new tab calculator',
			'calculator widget browser',
		],
	},

	{
		slug: 'pomodoro',
		name: 'Pomodoro Timer',
		category: 'Productivity',
		plan: 'free',
		model: 'PomodoroModel',
		title: 'Pomodoro Timer Chrome Extension for New Tab',
		metaDescription:
			'A pomodoro Chrome extension timer shaped like an hourglass that flips between phases. 25/5, 50/10 and 90/20 presets, and it keeps going if you close the tab.',
		h1: 'Pomodoro on the new tab, as an aesthetic study timer',
		lead:
			'This pomodoro timer lives on your Chrome new tab, and by default it’s an hourglass. Sand runs down while you focus, the glass turns over when the break starts. If you were after an aesthetic study timer rather than a red tomato, here it is.',
		screenshot: {
			file: 'pomodoro-hero.webp',
			alt:
				'Pomodoro timer as an hourglass on a glass card, 14:56 left in focus, three of four sessions done',
			caption: 'The sand is real time: ten minutes in, fifteen to go',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'An hourglass that flips',
				p: [
					'A new timer comes as Hourglass. The sand falls in step with the time that’s gone and changes colour with the phase, and when a phase ends (or you press Skip) the whole glass turns over. You can tell roughly how much is left from across the room without reading a digit.',
					'Ring and Minimal are the quieter options, one click away on the Design button. Dots under the timer show your place in the cycle, so three filled out of four means the long break is next.',
				],
			},
			{
				image: {
					file: 'pomodoro-settings.webp',
					alt:
						'Pomodoro settings: focus and break lengths, sessions before a long break, sound and Ring until I stop it',
					caption:
						'Durations, sound and a ring that waits for you to press Stop',
					width: 1440,
					height: 900,
					small: true,
				},
				h2: '25/5, 50/10 or 90/20',
				p: [
					'The presets cover the three rhythms people argue about. 25/5 with a 15-minute long break, 50/10 with 30, 90/20 with 30. Or set your own, with focus and breaks anywhere from 1 to 180 minutes and 1 to 10 focus sessions before the long break.',
					'Which one? Twenty-five minutes is short enough that starting doesn’t feel like a commitment. Fifty suits work that takes a while to get into — ninety is for the rare day with no meetings. Give the defaults a week before you tinker.',
					'Auto-start next session is off by default. Switch it on and each break rolls straight into the next focus block, so carrying on is the default and stopping is the decision.',
				],
			},
			{
				h2: 'Close the tab, the timer keeps going',
				p: [
					'The countdown runs off a finish time rather than the open page, so closing the tab does not stop it. Open another tab and the same timer is there, and when the phase ends only one tab rings.',
					'If no tab is open when time runs out, or the laptop was asleep, you won’t hear a thing. Next time you open a tab the widget says "Finished at 14:05" and waits for you; the next phase stays paused even with auto-start on.',
				],
			},
			{
				h2: 'The sound',
				p: [
					'The chimes are generated in the browser, no audio files involved. After focus the notes step down, after a break they step up, so you know which phase ended without looking. A normal alert lasts about six seconds.',
					'Easy to miss? Turn on Ring until I stop it and it keeps going for up to three minutes with a big Stop button, and a click or Esc works too. Stop silences every open tab at once. In a shared room, switch Sound at the end off and the timer becomes something you glance at instead.',
				],
				image: {
					file: 'pomodoro-alarm.webp',
					alt:
						'Pomodoro timer ringing with Focus complete, a large Stop button and an auto-stop countdown',
					caption:
						'It rings until you press Stop, for up to three minutes',
					width: 1440,
					height: 900,
					small: true,
				},
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Hourglass, Ring or Minimal. New timers start as Hourglass.',
			},
			{
				name: 'Presets',
				detail: '25/5, 50/10 or 90/20, each with its own long break.',
			},
			{
				name: 'Focus, Short break, Long break',
				detail:
					'From 1 to 180 minutes each. 25, 5 and 15 out of the box.',
			},
			{
				name: 'Focus sessions before a long break',
				detail: 'From 1 to 10, four by default.',
			},
			{
				name: 'Sound at the end',
				detail:
					'On by default. Test sound lets you hear it before a session.',
			},
			{
				name: 'Ring until I stop it',
				detail:
					'Up to three minutes of chimes, for when six seconds won’t pull you out of the work.',
			},
			{
				name: 'Auto-start next session',
				detail:
					'Off by default. Doesn’t fire after a phase that ended with no tab open.',
			},
			{
				name: 'Title',
				detail: 'Up to 24 characters, say "Thesis" or "Spanish".',
			},
		],
		useCases: [
			'Study sessions with an exam countdown on the same page.',
			'Fifty-minute writing blocks with ten minutes off.',
			'One task per focus block, taken from the list next to it.',
			'Long 90/20 rounds on a day with no meetings.',
		],
		faq: [
			{
				q: 'Is 25/5 or 50/10 Pomodoro better?',
				a:
					'Neither 25/5 nor 50/10 Pomodoro is better for everyone. 25/5 is the classic rhythm and the easier one to start, while 50/10 gives deep work time to warm up before you stop, and the ratio of work to rest is the same. Try each for a few days and keep the one you don’t fight. The widget has both as presets, with 15- and 30-minute long breaks.',
			},
			{
				q: 'Does Pomodoro work for ADHD?',
				a:
					'Pomodoro is not a treatment for ADHD, but some people with ADHD find it makes starting easier, because 25 minutes feels finite and the alarm takes over the job of noticing time. Others find a ringing timer breaks a rare stretch of deep focus. If that’s you, try the 50/10 or 90/20 preset, or switch the sound off and glance at the hourglass instead.',
			},
			{
				q: 'What is the 90/20 rule?',
				a:
					'The 90/20 rule means 90 minutes of focused work followed by a 20-minute break. The idea behind it is that attention comes in waves of roughly an hour and a half, so you stop before the wave runs out. It suits long, single tasks on days without meetings, and the widget has 90/20 as a preset with a 30-minute long break.',
			},
			{
				q: 'Is there a Pomodoro timer for the Chrome new tab?',
				a:
					'Chrome does not include a Pomodoro timer, though Google Search will run a plain countdown if you search for “25 minute timer”. For a proper Pomodoro on the new tab, New Tab Studio has a Pomodoro timer widget shaped like an hourglass that turns over when a phase ends. It sits under Productivity, in the panel behind the button in the bottom-left corner.',
			},
			{
				q: 'Does the Pomodoro timer keep running if I close the tab?',
				a:
					'Yes, the Pomodoro timer runs off a finish time, so closing the tab does not stop it. With another New Tab Studio tab open, the end of the phase rings there, in one tab only. With none open there’s no sound, and the next tab you open shows “Finished at 14:05” with the next phase paused.',
			},
		],
		related: ['todo-list', 'flash-cards', 'workday-countdown', 'timetable'],
		keywords: [
			'pomodoro chrome extension',
			'pomodoro timer chrome extension',
			'aesthetic study timer',
			'pomodoro new tab',
			'focus timer new tab',
		],
	},

	{
		slug: 'flash-cards',
		name: 'Flash Cards',
		category: 'Productivity',
		plan: 'free',
		model: 'FlashCardsModel',
		title: 'Flashcards Chrome Extension for New Tab',
		metaDescription:
			'A flashcards Chrome extension for vocabulary and exams: Leitner boxes, Again, Hard, Good and Easy grades, bulk import and a daily cap on new cards.',
		h1: 'Flash cards with spaced repetition on your new tab',
		lead:
			'Flashcards as a Chrome extension widget, on the page you open all day anyway. Spaced repetition works; what kills it is opening a separate app every single day. Here the next card is already waiting, face up, whenever you open a tab.',
		screenshot: {
			file: 'flash-cards-hero.webp',
			alt:
				'Spanish flash card showing the answer, with Again, Hard, Good and Easy buttons and their next intervals',
			caption:
				'Grade it and the card comes back in ten minutes or five days',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Four grades, not right or wrong',
				p: [
					'Flip a card and grade yourself with Again, Hard, Good or Easy. Each button is labelled with the wait it will set, so you see the consequence before you press.',
					'Again sends the card back to the very first box and shows it once more in ten minutes. Hard keeps it in the same box (box one at the lowest) and halves the wait. Good moves it up a box; Easy jumps two and makes the wait half as long again.',
					'Space or Enter turns the card over and keys 1 to 4 grade it, as long as the pointer is over the widget.',
				],
			},
			{
				h2: 'Leitner boxes you can see into',
				p: [
					'There are six boxes with waits of 0, 1, 3, 7, 14 and 30 days. Box zero is for new cards and anything you just failed. From box four, the fortnight, a card counts as learned, and the card list in the settings shows which box each one is in.',
					'The boxes are fixed, and only your grade stretches or shrinks the wait. So when a card keeps coming back you know why, and the fix is usually to rewrite it. A card you keep failing tends to be a badly written card, not a hard fact.',
				],
			},
			{
				h2: 'A cap on new cards',
				p: [
					'New cards per day is ten by default, adjustable from 1 to 100. Keep it low — sixty cards added in one keen evening with no cap turn into a week of enormous review sessions, and that’s how decks get abandoned.',
					'The newer designs keep score where you’ll see it, with chips for how many cards are due and how many are new, plus a progress bar for today. Clear the lot and it says "All done for today", tells you when the next review is, shows your day streak once it reaches two, and offers Review anyway if you’re on a roll.',
				],
				image: {
					file: 'flash-cards-deck.webp',
					alt:
						'Flash cards widget saying all done for today, next review in 3 hours, next to a countdown to a Spanish exam',
					caption: 'All done for today. The exam is eleven days out',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Building and sharing a deck',
				p: [
					'Add cards one at a time, Front and Back, or paste a list into Bulk import. One card per line, split by a semicolon, a vertical bar, a tab or a spaced dash. Rows copied out of a spreadsheet arrive as cards, and fronts already in the deck are skipped.',
					'The deck and its progress are stored in the widget. No account, no Anki or Quizlet sync, and an exported or published theme carries the whole deck, boxes and streak included. Press Reset progress before you share a vocabulary deck; the boxes go back to zero and the statistics stay.',
					'Flash cards joined the extension in version 4.0 with six designs, Classic, Flip card, Glass, Index card, Neon and Minimal. A new deck starts as Flip card, which turns over when you reveal the answer, and every design but Classic takes an accent colour.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Classic, Flip card, Glass, Index card, Neon or Minimal. New decks start as Flip card.',
			},
			{
				name: 'Deck name and cards',
				detail:
					'Front and back for each card, with its current box shown in the list. Deleting has an Undo.',
			},
			{
				name: 'Bulk import',
				detail:
					'One card per line, front and back split by ; or | or a tab or " - ". Repeated fronts are skipped.',
			},
			{
				name: 'New cards per day',
				detail:
					'Ten by default, from 1 to 100. The setting that decides whether the deck survives.',
			},
			{
				name: 'Shuffle',
				detail:
					'On by default, so you learn the card and not its place in the queue.',
			},
			{
				name: 'Show the back side first',
				detail:
					'Recall the other way round. See the meaning, produce the word.',
			},
			{
				name: 'Progress',
				detail:
					'Cards, Learned, Reviews and Day streak. Reset progress clears the boxes and keeps the statistics.',
			},
		],
		useCases: [
			'Vocabulary for a new language, ten new words a day.',
			'Exam revision next to a countdown to the exam.',
			'Terminology for a new job or course.',
			'A class deck published as a theme for everyone to install.',
		],
		faq: [
			{
				q:
					'Is there a free flashcard extension with spaced repetition?',
				a:
					'Yes, the Flash cards widget in New Tab Studio is a free flashcard extension with spaced repetition, built on Leitner boxes that bring a card back after 1, 3, 7, 14 or 30 days. The cards wait on your new tab, so reviewing doesn’t mean opening an app. A deck has no card limit and takes up a single free widget slot.',
			},
			{
				q: 'How does the Leitner system work?',
				a:
					'The Leitner system sorts flashcards into numbered boxes, moving a card you know into a box you review less often and a card you miss back to the first one. Shaky cards come up daily, solid ones rarely. Here there are six boxes with waits of 0, 1, 3, 7, 14 and 30 days, and a card counts as learned from the 14-day box.',
			},
			{
				q: 'What happens when I get a card wrong?',
				a:
					'In the Flash cards widget, a card you get wrong goes back to box zero when you press Again, and it returns in ten minutes. If you nearly had it, Hard is the kinder grade, keeping the card in its box at half the wait. Every button shows the wait it will set, so you see the consequence before you press.',
			},
			{
				q: 'Can I import cards from Anki or Quizlet?',
				a:
					'There’s no Anki or Quizlet sync, but Bulk import takes plain lines of text, one card per line, with front and back split by a semicolon, a vertical bar, a tab or a spaced dash. Rows copied out of a spreadsheet arrive as cards, and fronts already in the deck are skipped.',
			},
			{
				q: 'Will my deck follow me to another computer?',
				a:
					'A flash card deck won’t follow you to another computer on its own, since the widget has no account and no sync. Export your page or publish it as a theme, install that on the other machine, and the deck arrives with its boxes, due dates and streak. Sharing a class deck? Press Reset progress first, which sends every card back to box zero and keeps the statistics.',
			},
		],
		related: ['countdown', 'pomodoro', 'habit-tracker', 'timetable'],
		keywords: [
			'flashcards chrome extension',
			'vocabulary extension',
			'spaced repetition new tab',
			'flash cards new tab page',
			'leitner chrome extension',
		],
	},

	{
		slug: 'timetable',
		name: 'Timetable',
		category: 'Productivity',
		plan: 'free',
		model: 'TimetableModel',
		title: 'Class Timetable Widget for Chrome New Tab',
		metaDescription:
			'A timetable widget for classes and shifts on the Chrome new tab: a week grid, a list for today and a Next up card counting down to your next lesson.',
		h1: 'A class timetable on your new tab, with what’s next',
		lead:
			'A timetable widget for a week that repeats: lectures, classes, shifts. It doesn’t stop at the grid either. Put it on the new tab and it tells you what’s on right now, how long it has left and what comes after.',
		screenshot: {
			file: 'timetable-hero.webp',
			alt:
				'Weekly school timetable on glass, Monday to Friday, with the current Physics lesson highlighted on Wednesday',
			caption: 'Wednesday, 10:37: Physics, one hour 23 minutes to go',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Week, Today or Next up',
				p: [
					'Week is the grid, days across and hours down. Today turns the same lessons into a list for the day. The current one gets a progress bar and "1 hr 23 min left", later ones say how soon they start, and gaps show up as Break.',
					'Next up is the small one — and between classes, the most useful. It shows what’s on now with the room, a bar and the time left, then Next with a countdown. When the day is done it jumps to the first lesson of the next day that has one, "Tomorrow · 08:00" and so on.',
					'Each view has its own frame size. Next up fits under a clock; Week needs a proper chunk of the page.',
				],
				image: {
					file: 'timetable-today-next.webp',
					alt:
						'Two timetable views: today’s lessons with time left, and a card with the current and next lesson',
					caption:
						'Today’s list and what’s next, without the whole week',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Subject, room, colour',
				p: [
					'Click a cell in Week and type. The Subject goes on the first line, Room or note underneath, a lab number or a teacher’s name. Enter jumps down a row, Tab moves to the next day, Esc cancels.',
					'Every subject gets a colour from its name automatically, or you pick one. Subjects and colors in the settings lists them with a lesson count, and renaming a subject there renames every cell at once. Physics at 10 and Physics at 11 merge into one block from 10:00 to 12:00. One lesson, drawn once.',
				],
			},
			{
				h2: 'Your days, your hours',
				p: [
					'Days switch on one at a time. Monday to Friday out of the box, Monday to Saturday for a six-day school, or just Tuesday and Thursday. Week starts on Auto (it follows the interface language), Mon or Sun.',
					'Hours run 8:00 to 18:00 by default, so the last row starts at 17:00. Rows can be 30 or 45 minutes, an hour, an hour and a half, or two. Lessons are pinned to their time rather than to a row, so switching to 45-minute rows or a later start scrambles nothing. Whatever falls outside the new range is hidden, not deleted, and the settings say how many lessons that is.',
				],
			},
			{
				h2: 'Now, highlighted',
				p: [
					'With Highlight today and the current time on, the week grid marks today’s column, draws a line at the current time and highlights the lesson you’re in. It refreshes on the minute and again the moment you come back to the tab.',
					'It will not send a notification before class. The substitute is Next up on the page, where "in 10 min" is hard to miss.',
				],
			},
		],
		settings: [
			{ name: 'View', detail: 'Week, Today or Next up.' },
			{
				name: 'Title',
				detail: 'Up to 70 characters. Next up doesn’t show it.',
			},
			{
				name: 'Highlight today and the current time',
				detail:
					'Week view only. Today’s column, a line for now and the current lesson.',
			},
			{
				name: 'Edit timetable',
				detail: 'Opens the week editor, even from Today or Next up.',
			},
			{
				name: 'Days',
				detail:
					'Seven buttons, any combination, at least one. Monday to Friday on a new timetable.',
			},
			{
				name: 'Week starts on',
				detail:
					'Auto, Mon or Sun. Auto goes by the interface language.',
			},
			{
				name: 'Start hour, End hour, Row length',
				detail:
					'8, 18 and one hour by default. Rows run from 30 minutes to two hours, up to 48 of them.',
			},
			{
				name: 'Subjects and colors',
				detail:
					'Every subject with its lesson count. Recolour or rename it everywhere at once.',
			},
			{
				name: 'Design',
				detail:
					'Classic, Light, Dark or Glass. New timetables start in Glass (Light or Dark on a plain background).',
			},
		],
		useCases: [
			'A university timetable next to an exam countdown.',
			'Next up under the clock, so the next lesson is one glance away.',
			'A six-day shift pattern.',
			'A teaching week, rooms included.',
		],
		faq: [
			{
				q: 'How can I keep my class schedule on my computer screen?',
				a:
					'The usual ways to keep a class schedule on screen are a wallpaper with the timetable on it, a calendar full of repeating events, or a timetable widget on the new tab. A wallpaper can’t tell you what’s next, and twenty weekly events clutter a calendar. New Tab Studio’s Timetable widget adds a Next up view with the current lesson, its time left and a countdown to the next.',
			},
			{
				q: 'Do I need PRO for the timetable?',
				a:
					'No, the Timetable widget has nothing locked behind PRO, from the three views to subject colours and the week editor. It arrived in version 4.0 and just occupies a free slot. There are three of those for widgets you place by hand, and anything that came with an installed theme leaves them alone.',
			},
			{
				q: 'How is the timetable different from the Calendar widget?',
				a:
					'The Calendar widget holds dated events that happen once, while Timetable holds a week that repeats and has no dates at all. Twenty weekly lectures in a calendar is clutter; in a timetable it’s one tidy grid. Plenty of students run both, the timetable for the routine and the calendar for exams and deadlines.',
			},
			{
				q: 'Can I show weekdays only, or six days?',
				a:
					'Yes, timetable days switch on one at a time, so Monday to Friday, Monday to Saturday or just Tuesday and Thursday all work. A new timetable starts on Monday to Friday. Week starts on follows your interface language until you set Mon or Sun yourself.',
			},
			{
				q: 'Does the timetable remind me before a class?',
				a:
					'No, the timetable sends no notifications. Keep the Next up view on the page instead, where “in 10 min” counts down to the next lesson and the current one shows how long it has left. Once the day is over, it jumps ahead to the first lesson of the next day that has one.',
			},
		],
		related: ['calendar', 'countdown', 'pomodoro', 'flash-cards'],
		keywords: [
			'timetable widget',
			'class timetable',
			'timetable chrome extension',
			'weekly schedule new tab',
			'class schedule new tab',
		],
	},

	{
		slug: 'google-calendar',
		name: 'Google Calendar',
		category: 'Productivity',
		plan: 'free',
		model: 'GoogleCalendarModel',
		title: 'Google Calendar Widget for Chrome New Tab',
		metaDescription:
			'A Google Calendar widget for your Chrome new tab: any Google Calendar embed in a frame, on every tab. No account to link to us, no permissions to grant.',
		h1: 'Google Calendar widget for your Chrome new tab',
		lead:
			'This Google Calendar widget puts Google’s own calendar page in a frame on your new tab. You choose the address, and the extension shows it on every tab. It is not a separate Google Calendar Chrome extension, and it isn’t made by Google; it’s one of the widgets in New Tab Studio.',
		screenshot: {
			file: 'google-calendar-hero.webp',
			alt:
				'Embedded Google Calendar week view with Columbus Day, next to a todo list on the Work workspace',
			caption: 'Google Calendar’s week view, right on the Work workspace',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Two calendar widgets, two jobs',
				p: [
					'This one embeds Google Calendar itself. Google draws the page and holds the events, and whatever Google allows inside a frame is what you get. We never see any of it.',
					'The Calendar widget is ours. It keeps its own events in the browser and can import a Google Calendar one way through the secret iCal address, read only, next to your own entries. Want Google’s own week view and the calendars your team shares? Use this one. Want something that opens without loading Google’s page on every tab? Use Calendar.',
				],
			},
			{
				h2: 'Setting it up',
				p: [
					'Open the + panel, go to Productivity and choose Google Calendar. The settings window opens straight away with one field, URL, and two shortcuts, Default embed and Full calendar.',
					'For your own mix of calendars, colours, or a week view to start from, click Customize embed. Google’s tool builds an address, you paste it into URL, and the widget shows it exactly as given. Week or month is part of that address rather than a widget setting.',
					'The address saves itself a moment after you stop typing, so the frame no longer reloads on every character.',
				],
				image: {
					file: 'google-calendar-settings.webp',
					alt:
						'Google Calendar widget settings with a URL field and Default embed, Full calendar and help links',
					caption:
						'Paste your calendar’s embed link, or start from one of these',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'What shows up in the frame',
				p: [
					'Public calendars display fine — private events depend on Google. They can appear if you’re signed in to Google in the same Chrome profile and the browser lets the frame use that sign-in. If it doesn’t, the frame won’t show them, and no setting in the widget changes that.',
					'Full calendar points the frame at calendar.google.com itself. Whether Google runs its full app inside a frame is Google’s call, so treat it as worth a try rather than a promise. The same goes for editing events. Count on reading your week here and editing it in Google Calendar proper.',
				],
			},
			{
				h2: 'The cost of a frame',
				p: [
					'An embedded page loads with every new tab, and Google Calendar is not a small page. Keep it on a work workspace instead of the page you open all day, and give it width; a week view squeezed into a narrow column can’t be read.',
					'In edit mode the frame is covered by a notice that its content is locked, so moving the widget around never clicks into the calendar. Leave edit mode and it’s live again.',
				],
			},
		],
		settings: [
			{
				name: 'URL',
				detail:
					'Any Google Calendar address, embed links included. Saved a moment after you stop typing.',
			},
			{
				name: 'Default embed',
				detail: 'Google’s standard embed page, one click to fill in.',
			},
			{
				name: 'Full calendar',
				detail:
					'calendar.google.com itself. Whether it loads in a frame is up to Google.',
			},
			{
				name: 'Customize embed',
				detail:
					'Opens Google’s embed builder to pick calendars, colours and the starting view.',
			},
			{
				name: 'Size and position',
				detail:
					'A week view needs real width, more than any other widget on the page.',
			},
		],
		useCases: [
			'A shared team calendar on a work workspace.',
			'Google’s week view beside a to-do list.',
			'A public calendar, like holidays or a club’s fixtures.',
			'Checking the day without opening a calendar tab.',
		],
		faq: [
			{
				q: 'How do I put Google Calendar on my Chrome new tab page?',
				a:
					'Chrome’s own new tab can only hold a shortcut tile to calendar.google.com, so seeing the calendar itself there takes an extension. In New Tab Studio, open the widget panel with the button in the bottom-left corner, choose Productivity, then Google Calendar, and pick Default embed or paste your own embed link into the URL field. Then give the widget plenty of width.',
			},
			{
				q: 'How do I put Google Calendar on my desktop?',
				a:
					'A Google Calendar widget can sit on your browser’s new tab, but not on the Windows or macOS desktop itself. If the desktop you mean is the browser, add this widget and every new tab shows the calendar. For the system desktop, the usual route is a calendar app with your Google account added.',
			},
			{
				q: 'Is the Google Calendar widget free?',
				a:
					'Yes, the Google Calendar widget is free and has no PRO features at all. On the free plan it uses one of three spaces for widgets you add by hand, and widgets from an installed theme don’t touch those spaces. There’s no account with us to create either, only the Google one you already have.',
			},
			{
				q: 'Do I have to connect my Google account?',
				a:
					'You don’t connect your Google account to New Tab Studio at all. The widget asks for no permissions and never touches your calendar; it shows Google’s page in a frame, and any sign-in is between you and Google. Private events appear only if Google lets the frame use the sign-in from your Chrome profile.',
			},
			{
				q: 'Can I edit events from the widget?',
				a:
					'Editing events inside the Google Calendar widget may work, depending on the address in the frame and on Google, so we won’t promise it. Reading your week at a glance is the job it’s built for. If you’d rather skip the frame, the Calendar widget can import a Google Calendar read-only through its iCal address.',
			},
		],
		related: ['calendar', 'todo-list', 'workspaces', 'iframe'],
		keywords: [
			'google calendar widget',
			'google calendar chrome extension',
			'how to put google calendar on desktop',
			'google calendar new tab',
			'embed google calendar new tab',
		],
	},
];
